<ngx-spinner type="ball-atom"></ngx-spinner>
<div class="component-container">
  <div class="component-header">
    <div class="component-header-title">
      <h2>Customer Reports</h2>
      <p class="component-header-subtitle">
        Rebuild the precalculated company-report table. Each row stores every variable value
        for one (company, year) pair, so customer report exports stream straight from this table
        without re-running the category resolvers. A scheduled job rebuilds it nightly; use this
        page to trigger an immediate rebuild after a large data-entry batch.
      </p>
    </div>
  </div>

  @if (lastRebuild) {
    <div class="last-updated">
      <mat-icon>schedule</mat-icon>
      <span>
        Last updated <strong>{{ lastRebuild.executedAt | date:'medium' }}</strong>
        ({{ triggerDescription(lastRebuild) }}) —
        {{ formatNumber(lastRebuild.rowsInserted) }} rows in {{ formatDuration(lastRebuild.elapsedMs) }}
      </span>
    </div>
  } @else {
    <div class="last-updated none">
      <mat-icon>schedule</mat-icon>
      <span>This table has not been rebuilt yet.</span>
    </div>
  }

  <form [formGroup]="form" class="rebuild-form">
    <mat-form-field appearance="fill" subscriptSizing="dynamic">
      <mat-label>From Year</mat-label>
      <input matInput type="number" formControlName="fromYear" placeholder="Auto" />
      <mat-hint>Blank = earliest year</mat-hint>
    </mat-form-field>

    <mat-form-field appearance="fill" subscriptSizing="dynamic">
      <mat-label>To Year</mat-label>
      <input matInput type="number" formControlName="toYear" placeholder="Auto" />
      <mat-hint>Blank = current year</mat-hint>
    </mat-form-field>

    <mat-form-field appearance="fill" subscriptSizing="dynamic">
      <mat-label>Batch Size</mat-label>
      <input matInput type="number" formControlName="batchSize" />
      <mat-hint>Companies per pass</mat-hint>
    </mat-form-field>

    <button
      mat-flat-button
      color="primary"
      type="button"
      [disabled]="rebuilding || form.invalid"
      (click)="rebuild()">
      <mat-icon>autorenew</mat-icon>
      {{ rebuilding ? 'Rebuilding…' : 'Rebuild Table' }}
    </button>
  </form>

  @if (rebuilding && startedAt) {
    <div class="status-card running">
      <mat-icon>hourglass_top</mat-icon>
      <div>
        <strong>Rebuild in progress</strong>
        <p>Started {{ startedAt | date:'medium' }}. Keep this tab open until it completes.</p>
      </div>
    </div>
  }

  @if (error) {
    <div class="status-card error">
      <mat-icon>error_outline</mat-icon>
      <div>
        <strong>Rebuild failed</strong>
        <p>{{ error }}</p>
      </div>
    </div>
  }

  @if (result) {
    <div class="result-section">
      <h3>Last Rebuild</h3>

      <div class="summary-grid">
        <div class="summary-item">
          <span class="summary-label">Total Time</span>
          <span class="summary-value">{{ formatDuration(result.totalMs) }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Rows Inserted</span>
          <span class="summary-value">{{ formatNumber(result.rowsInserted) }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Companies</span>
          <span class="summary-value">{{ formatNumber(result.companyCount) }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Variables</span>
          <span class="summary-value">{{ formatNumber(result.variableCount) }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Year Range</span>
          <span class="summary-value">{{ result.fromYear }}–{{ result.toYear }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Batches</span>
          <span class="summary-value">{{ result.batchCount }} × {{ result.batchSize }}</span>
        </div>
      </div>

      <h4>Stage Breakdown</h4>
      <table class="stage-table">
        <tr><td>Schema Setup</td><td>{{ formatDuration(result.schemaSetupMs) }}</td></tr>
        <tr><td>Activity Lookup</td><td>{{ formatDuration(result.activityMs) }}</td></tr>
        <tr><td>Resolvers (total)</td><td>{{ formatDuration(result.resolversMs) }}</td></tr>
        <tr><td>Insert (COPY)</td><td>{{ formatDuration(result.insertMs) }}</td></tr>
        <tr><td>Atomic Swap</td><td>{{ formatDuration(result.swapMs) }}</td></tr>
      </table>

      <h4>Per-Resolver Timings</h4>
      <table mat-table [dataSource]="result.resolverTimings" class="resolver-table">
        <ng-container matColumnDef="category">
          <th mat-header-cell *matHeaderCellDef>Category</th>
          <td mat-cell *matCellDef="let r">{{ r.category }}</td>
        </ng-container>
        <ng-container matColumnDef="elapsedMs">
          <th mat-header-cell *matHeaderCellDef>Elapsed</th>
          <td mat-cell *matCellDef="let r">{{ formatDuration(r.elapsedMs) }}</td>
        </ng-container>
        <ng-container matColumnDef="cellsProduced">
          <th mat-header-cell *matHeaderCellDef>Cells</th>
          <td mat-cell *matCellDef="let r">{{ formatNumber(r.cellsProduced) }}</td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedTimingColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedTimingColumns;"></tr>
      </table>
    </div>
  }

  @if (history.length > 0) {
    <div class="result-section">
      <h3>Recent Rebuilds</h3>
      <table mat-table [dataSource]="history" class="history-table">
        <ng-container matColumnDef="executedAt">
          <th mat-header-cell *matHeaderCellDef>When</th>
          <td mat-cell *matCellDef="let r">{{ r.executedAt | date:'medium' }}</td>
        </ng-container>
        <ng-container matColumnDef="trigger">
          <th mat-header-cell *matHeaderCellDef>Trigger</th>
          <td mat-cell *matCellDef="let r">{{ triggerLabel(r.trigger) }}</td>
        </ng-container>
        <ng-container matColumnDef="triggeredBy">
          <th mat-header-cell *matHeaderCellDef>By</th>
          <td mat-cell *matCellDef="let r">{{ r.triggeredBy }}</td>
        </ng-container>
        <ng-container matColumnDef="rowsInserted">
          <th mat-header-cell *matHeaderCellDef>Rows</th>
          <td mat-cell *matCellDef="let r">{{ formatNumber(r.rowsInserted) }}</td>
        </ng-container>
        <ng-container matColumnDef="elapsedMs">
          <th mat-header-cell *matHeaderCellDef>Elapsed</th>
          <td mat-cell *matCellDef="let r">{{ formatDuration(r.elapsedMs) }}</td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedHistoryColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedHistoryColumns;"></tr>
      </table>
    </div>
  }
</div>
